/**
 * Welcome Wizard Component
 *
 * Multi-step modal that appears on first login to capture operating profile selection and pricing.
 * This ensures users are properly onboarded based on their fleet type.
 *
 * Steps:
 * 1. Welcome Screen - Introduction to PCNGuard Fleet
 * 2. Operating Profile Selection - Choose the workflow to set up first
 * 3. Company Details - Confirm setup route
 * 4. Pricing Selection - Choose subscription tier
 */

import React, { useMemo, useState } from 'react';
import { ShieldCheck, Car, ArrowRight, CheckCircle2, type LucideIcon } from 'lucide-react';
import { BusinessModel } from '../types';
import { supabase } from '../services/supabaseClient';
import { PricingStepCard } from './PricingStepCard';
import { ApiService } from '../services/api';

interface WelcomeWizardProps {
  onComplete: () => void;
  organizationId: string;
}

interface BusinessModelCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  color: 'primary' | 'blue';
}

const BusinessModelCard: React.FC<BusinessModelCardProps> = ({
  icon: Icon,
  title,
  description,
  selected,
  onClick,
  color
}) => {
  const colorClasses = {
    primary: {
      border: 'border-primary-500 bg-primary-50 dark:bg-primary-900/20',
      iconBg: 'bg-primary-100 dark:bg-primary-800',
      iconColor: 'text-primary-600 dark:text-primary-400'
    },
    blue: {
      border: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
      iconBg: 'bg-blue-100 dark:bg-blue-800',
      iconColor: 'text-blue-600 dark:text-blue-400'
    }
  };

  const colors = colorClasses[color];

  return (
    <button
      onClick={onClick}
      className={`text-left border-2 rounded-xl p-6 transition-all ${
        selected
          ? colors.border
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`p-3 rounded-lg ${
            selected ? colors.iconBg : 'bg-slate-100 dark:bg-slate-800'
          }`}
        >
          <Icon
            size={24}
            className={selected ? colors.iconColor : 'text-slate-400'}
          />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 dark:text-white text-lg mb-1">
            {title}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {description}
          </p>
        </div>
      </div>
    </button>
  );
};

export const WelcomeWizard: React.FC<WelcomeWizardProps> = ({
  onComplete,
  organizationId
}) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [businessModel, setBusinessModel] = useState<BusinessModel | null>(null);
  const [ticketEstimate, setTicketEstimate] = useState(60);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectBusinessModel = (model: BusinessModel) => {
    setBusinessModel(model);
  };

  const saveOrganizationSettings = async (tier: 'starter' | 'growth' | 'pro_fleet' | 'concierge') => {
    if (!businessModel) throw new Error('No business model selected');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const legal_entity_verified = false;
    const legal_entity_verification_status =
      businessModel === 'hire_firm_registered'
        ? 'unverified'
        : 'not_applicable';

    // Save business model and subscription tier to organization
    const { error: orgError } = await supabase
      .from('organisations')
      .update({
        business_model: businessModel,
        legal_entity_verified,
        legal_entity_verification_status,
        subscription_tier: tier,
        enable_hire_workflows: businessModel !== 'fleet_operator',
        enable_transfer_features: businessModel !== 'fleet_operator'
      })
      .eq('id', organizationId);

    if (orgError) throw orgError;
  };

  const markWizardCompleted = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error: prefError } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: user.id,
        preferences: { welcome_wizard_completed: true },
        updated_at: new Date().toISOString()
      });

    if (prefError) throw prefError;
  };

  const handleSkipPricing = async () => {
    setSaving(true);
    setError(null);

    try {
      await saveOrganizationSettings('starter');
      await markWizardCompleted();
      onComplete();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
      setSaving(false);
    }
  };

  const handlePricingSelection = async (tier: 'growth' | 'pro_fleet') => {
    setSaving(true);
    setError(null);

    try {
      // Save settings first
      await saveOrganizationSettings(tier);
      await markWizardCompleted();

      // Create Stripe checkout session
      const { url } = await ApiService.createCheckoutSession(tier, 'monthly');

      // Redirect to Stripe
      window.location.href = url;
    } catch (err) {
      console.error('Error starting checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setSaving(false);
    }
  };

  const handleContactSales = () => {
    alert('The FleetMode team will contact you for a Concierge plan review. Click OK to continue with Starter for now.');
    handleSkipPricing();
  };

  const estimate = useMemo(() => {
    const tickets = Number.isFinite(ticketEstimate) ? Math.max(0, Math.floor(ticketEstimate)) : 0;
    const adminFee = 20;
    const workflowValue = tickets * adminFee;
    const growthPlan = 179;
    const planShare = workflowValue > 0 ? Math.round((growthPlan / workflowValue) * 100) : 0;

    return { tickets, starterTotal: 79, proTotal: 299, savings: planShare };
  }, [ticketEstimate]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className={`bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full p-8 max-h-[90vh] overflow-y-auto ${
          step === 4 ? 'max-w-6xl' : 'max-w-2xl'
        }`}
      >
        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto">
              <ShieldCheck className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                Welcome to PCNGuard Fleet
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Let's personalize your experience. This will only take a minute.
              </p>
            </div>
            <button
              onClick={() => setStep(2)}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
            >
              Get Started <ArrowRight size={20} />
            </button>
          </div>
        )}

        {/* Step 2: Operating Profile Selection */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Which workflow do you want to set up first?
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                FleetMode is built for PCO hire teams that need to match PCNs to hirers,
                check agreement evidence, prepare transfer packs, and track recovery.
              </p>
            </div>

            <div className="grid gap-4">
              {/* PCO Hire Operator Card */}
              <BusinessModelCard
                icon={ShieldCheck}
                title="PCO Hire Operator"
                description="Match PCNs to hirers, check hire-agreement evidence, prepare transfer packs, and track admin-fee recovery."
                selected={businessModel === 'hire_firm_registered'}
                onClick={() => selectBusinessModel('hire_firm_registered')}
                color="primary"
              />

              {/* Owned / Managed Fleet Card */}
              <BusinessModelCard
                icon={Car}
                title="Owned / Managed Fleet"
                description="Manage company vehicles used by staff or contractors. Pay, appeal, or assign tickets internally."
                selected={businessModel === 'fleet_operator'}
                onClick={() => selectBusinessModel('fleet_operator')}
                color="blue"
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!businessModel}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
              >
                Continue <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Company Details (conditional) */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {businessModel === 'fleet_operator'
                  ? 'Almost done!'
                  : 'PCO setup details'}
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                {businessModel === 'fleet_operator'
                  ? 'Continue to choose a plan (you can skip for now).'
                  : 'Next, set up the hire records and agreement evidence FleetMode uses to prepare transfer packs.'}
              </p>
            </div>

            {businessModel === 'hire_firm_registered' && (
              <div className="pb-6">
                <div className="p-4 border border-primary-200 dark:border-primary-800 rounded-lg bg-primary-50/60 dark:bg-primary-900/20">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        FleetMode will guide your team through transfer evidence.
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                        Transfer pack readiness is checked per PCN using hire dates, a signed agreement,
                        hirer details, and liability wording. Organisation setup alone never marks a PCN ready.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(2)}
                disabled={saving}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={() => setStep(4)}
                disabled={saving}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
              >
                Continue <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Pricing Selection */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Choose Your Plan
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Paid plans include unlimited PCNs. Choose based on seats, reporting depth, evidence tools, and support.
              </p>
            </div>

            {/* Billing explainer + quick estimate */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-5">
                <h3 className="font-bold text-primary-900 dark:text-primary-100 mb-3">
                  How billing works
                </h3>
                <ul className="space-y-2 text-sm text-primary-900 dark:text-primary-100">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <span><strong>Platform subscription</strong> unlocks features (e.g. automation, analytics).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <span><strong>Unlimited PCNs</strong> are included on every paid plan.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <span><strong>No per-PCN charges</strong> are added to Starter, Growth, or Pro Fleet.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <span><strong>Growth and Pro Fleet</strong> unlock team access, deeper reporting, and evidence workflows.</span>
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <h3 className="font-bold text-slate-900 dark:text-white">Quick estimate</h3>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Tickets / month
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={ticketEstimate}
                    onChange={(e) => setTicketEstimate(Number(e.target.value))}
                    className="w-28 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <div className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
                    Uses a GBP 20 admin-fee workflow anchor.
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 dark:text-slate-300">Starter plan</span>
                    <span className="font-bold text-slate-900 dark:text-white">£{estimate.starterTotal}/mo</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 dark:text-slate-300">Pro Fleet plan</span>
                    <span className="font-bold text-slate-900 dark:text-white">£{estimate.proTotal}/mo</span>
                  </div>
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <span className="text-slate-600 dark:text-slate-300">Growth share of workflow</span>
                    <span className="font-bold text-primary-700 dark:text-primary-300">{estimate.savings}%</span>
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    Estimates exclude any managed/concierge pricing and taxes.
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-stretch">
              <PricingStepCard
                tier="starter"
                onSelect={handleSkipPricing}
              />
              <PricingStepCard
                tier="growth"
                highlighted
                onSelect={() => handlePricingSelection('growth')}
              />
              <PricingStepCard
                tier="pro_fleet"
                onSelect={() => handlePricingSelection('pro_fleet')}
              />
              <PricingStepCard
                tier="concierge"
                onSelect={handleContactSales}
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
              </div>
            )}

            {/* Skip Button */}
            <div className="text-center pt-2">
              <button
                onClick={handleSkipPricing}
                disabled={saving}
                className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors disabled:opacity-50"
              >
                Skip for now - continue with Starter
              </button>
            </div>

            <div className="flex justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setStep(3)}
                disabled={saving}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors disabled:opacity-50"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="mt-8 flex items-center justify-center gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === step
                  ? 'bg-primary-600 w-8'
                  : i < step
                  ? 'bg-primary-400 w-2'
                  : 'bg-slate-300 dark:bg-slate-700 w-2'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
